import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import { Lock, Save, Eye, EyeOff, ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";

const NEXON = { indigo: "#636AC3", ink: "#1E1E3F", mint: "#C9E9A4", cloud: "#FAF9FE", ash: "#7A7A99" };

function scorePassword(pw) {
  if (!pw) return { score: 0, label: "—", tone: "slate" };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const map = [
    { label: "too short",     tone: "rose"    },
    { label: "weak",          tone: "rose"    },
    { label: "fair",          tone: "amber"   },
    { label: "good",          tone: "emerald" },
    { label: "strong",        tone: "emerald" },
    { label: "excellent",     tone: "emerald" },
  ];
  return { score: s, ...map[s] };
}

const TONE = {
  slate:   "bg-slate-100 text-slate-600",
  rose:    "bg-rose-100 text-rose-700",
  amber:   "bg-amber-100 text-amber-700",
  emerald: "bg-emerald-100 text-emerald-700",
};

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1.5">{hint}</span>}
    </label>
  );
}

export default function AdminSettings() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const strength = scorePassword(form.new_password);
  const match = form.confirm && form.new_password && form.confirm === form.new_password;

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (form.new_password !== form.confirm) { toast.error("New password and confirmation don't match"); return; }
    if (form.new_password === form.current_password) { toast.error("New password must be different from the current one"); return; }
    setLoading(true);
    try {
      await adminApi.post("/admin/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated. Use the new one on your next login.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update password");
    } finally { setLoading(false); }
  };

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 lg:p-8" style={{ backgroundColor: NEXON.cloud }} data-testid="admin-settings">
        <div>
          <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: NEXON.indigo }}>Settings</span>
          <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-1" style={{ color: NEXON.ink }}>
            Account &amp; security
          </h1>
          <p className="text-sm mt-1" style={{ color: NEXON.ash }}>
            Change your admin password. Every change is logged to the audit trail.
          </p>
          <div className="mt-3 h-[3px] w-24 rounded-full" style={{ backgroundImage: `linear-gradient(90deg, ${NEXON.indigo}, ${NEXON.ink}, ${NEXON.mint})` }} />
        </div>

        <div className="mt-8 max-w-2xl">
          <form
            onSubmit={submit}
            data-testid="admin-change-password-form"
            className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: NEXON.mint, color: NEXON.ink }}>
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-black text-lg leading-tight" style={{ color: NEXON.ink }}>Change password</h2>
                <p className="text-xs text-slate-500 mt-0.5">Used to sign into the admin console at <span className="font-mono text-slate-700">/admin/login</span>.</p>
              </div>
            </div>

            <Field label="Current password">
              <div className="relative">
                <input
                  data-testid="settings-current-password"
                  type={showCurrent ? "text" : "password"}
                  value={form.current_password}
                  onChange={onChange("current_password")}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full h-12 pl-4 pr-14 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  aria-label={showCurrent ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  data-testid="settings-current-toggle"
                >
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </Field>

            <Field label="New password" hint="Minimum 8 characters. Mix upper &amp; lower case, numbers, and at least one symbol for best security.">
              <div className="relative">
                <input
                  data-testid="settings-new-password"
                  type={showNew ? "text" : "password"}
                  value={form.new_password}
                  onChange={onChange("new_password")}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  className="w-full h-12 pl-4 pr-14 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  data-testid="settings-new-toggle"
                >
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {form.new_password && (
                <div className="mt-2 flex items-center gap-2" data-testid="settings-strength">
                  <div className="flex-1 flex gap-1">
                    {[0,1,2,3,4].map((i) => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full ${i < strength.score ? (strength.tone === "rose" ? "bg-rose-500" : strength.tone === "amber" ? "bg-amber-500" : "bg-emerald-500") : "bg-slate-200"}`} />
                    ))}
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${TONE[strength.tone]}`}>{strength.label}</span>
                </div>
              )}
            </Field>

            <Field label="Confirm new password">
              <input
                data-testid="settings-confirm-password"
                type={showNew ? "text" : "password"}
                value={form.confirm}
                onChange={onChange("confirm")}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="••••••••"
                className={`w-full h-12 px-4 rounded-xl border bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none text-sm font-mono ${form.confirm && !match ? "border-rose-300 focus:border-rose-400" : "border-slate-200 focus:border-indigo-400"}`}
              />
              {form.confirm && !match && (
                <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-rose-600 font-black" data-testid="settings-match-error">
                  <AlertTriangle size={12} /> Doesn't match the new password.
                </span>
              )}
              {form.confirm && match && (
                <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-emerald-600 font-black" data-testid="settings-match-ok">
                  <ShieldCheck size={12} /> Match.
                </span>
              )}
            </Field>

            <button
              type="submit"
              disabled={loading}
              data-testid="settings-save-password"
              className="w-full h-12 rounded-xl text-white font-black inline-flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 transition-colors hover:opacity-95"
              style={{ backgroundColor: NEXON.ink }}
            >
              <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save new password"}
            </button>

            <div className="pt-2 flex items-start gap-2 text-[11px] text-slate-500">
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: NEXON.indigo }} />
              <span>
                Every password change is <span className="font-black">bcrypt-hashed</span> and appended to the audit trail. The old env-based password is retired after the first change.
              </span>
            </div>
          </form>

          <p className="text-[11px] text-slate-400 px-1 mt-4">
            Mailbox, persona, auto-send, alerts, banned phrases &amp; reply templates live under Support Inbox → Settings.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
